import React, { useMemo, useEffect, useState } from 'react'
import { getIcon } from '../icons'
import AxisDropZone from './AxisDropZone'

const MeasureIcon = getIcon('measure')
const DimensionIcon = getIcon('dimension')
const TimeDimensionIcon = getIcon('timeDimension')
import { chartConfigRegistry } from '../charts/chartConfigRegistry'
import { getChartConfig } from '../charts/chartConfigs'
import type { ChartType, ChartAxisConfig, ChartDisplayConfig, ColorPalette } from '../types'

interface ChartConfigPanelProps {
  chartType: ChartType
  chartConfig: ChartAxisConfig
  displayConfig: ChartDisplayConfig
  availableFields: {
    dimensions: string[]
    timeDimensions: string[]
    measures: string[]
  } | null
  colorPalette?: ColorPalette
  onChartConfigChange: (config: ChartAxisConfig) => void
  onDisplayConfigChange: (config: ChartDisplayConfig) => void
}

export default function ChartConfigPanel({
  chartType,
  chartConfig,
  displayConfig,
  availableFields,
  colorPalette,
  onChartConfigChange,
  onDisplayConfigChange
}: ChartConfigPanelProps) {
  
  // Track currently dragging item for immediate state updates
  const [draggedItem, setDraggedItem] = useState<{
    field: string
    fromAxis: string
    fromIndex?: number
  } | null>(null)
  
  // Get configuration for current chart type
  const chartTypeConfig = useMemo(() => 
    getChartConfig(chartType, chartConfigRegistry),
    [chartType]
  )
  
  // Check if this chart type skips queries
  const shouldSkipQuery = chartTypeConfig.skipQuery === true

  // Get fields for each drop zone
  const getFieldsForDropZone = (key: string): string[] => {
    const value = chartConfig[key as keyof ChartAxisConfig]
    const result = Array.isArray(value) ? value : (typeof value === 'string' ? [value] : [])
    return result
  }

  // Clean up chart config when available fields change
  useEffect(() => {
    if (!availableFields) return

    const allAvailableFields = [
      ...availableFields.dimensions,
      ...availableFields.timeDimensions,
      ...availableFields.measures
    ]

    let hasChanges = false
    const newConfig = { ...chartConfig }

    // Check each axis and remove fields that are no longer available
    chartTypeConfig.dropZones.forEach(dropZone => {
      const currentFields = getFieldsForDropZone(dropZone.key)
      const validFields = currentFields.filter(field => allAvailableFields.includes(field))
      
      if (validFields.length !== currentFields.length) {
        hasChanges = true
        if (validFields.length === 0) {
          // Remove the axis property entirely if no valid fields remain
          delete newConfig[dropZone.key as keyof ChartAxisConfig]
        } else if (dropZone.maxItems === 1) {
          // Single field axis - always store as string
          newConfig[dropZone.key as keyof ChartAxisConfig] = validFields[0] as any
        } else {
          // Multi-field axis - always store as array
          newConfig[dropZone.key as keyof ChartAxisConfig] = validFields as any
        }
      }
    })

    if (hasChanges) {
      onChartConfigChange(newConfig)
    }
  }, [availableFields, chartConfig, chartTypeConfig.dropZones, onChartConfigChange])

  // Helper to determine field type and styling
  const getFieldType = (field: string): 'dimension' | 'timeDimension' | 'measure' => {
    if (!availableFields) return 'dimension'
    if (availableFields.measures.includes(field)) return 'measure'
    if (availableFields.timeDimensions.includes(field)) return 'timeDimension'
    return 'dimension'
  }

  const getFieldStyling = (field: string) => {
    const fieldType = getFieldType(field)

    switch (fieldType) {
      case 'measure':
        return {
          IconComponent: MeasureIcon,
          baseClasses: 'bg-dc-measure text-dc-measure border border-dc-measure',
          hoverClasses: 'hover:opacity-80'
        }
      case 'timeDimension':
        return {
          IconComponent: TimeDimensionIcon,
          baseClasses: 'bg-dc-time-dimension text-dc-time-dimension border border-dc-time-dimension',
          hoverClasses: 'hover:opacity-80'
        }
      default:
        return {
          IconComponent: DimensionIcon,
          baseClasses: 'bg-dc-dimension text-dc-dimension border border-dc-dimension',
          hoverClasses: 'hover:opacity-80'
        }
    }
  }

  // Drag and drop handlers
  const handleDragStart = (e: React.DragEvent<HTMLDivElement>, field: string, fromAxis: string, fromIndex?: number) => {
    e.dataTransfer.setData('text/plain', JSON.stringify({ field, fromAxis, fromIndex }))
    
    // Just track the dragged item - don't remove it yet
    setDraggedItem({ field, fromAxis, fromIndex })
  }

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
  }

  const handleDragEnd = () => {
    // Just clear the dragged item tracking - no need to restore since we didn't remove it
    setDraggedItem(null)
  }

  const handleDrop = (e: React.DragEvent<HTMLDivElement>, toAxis: string) => {
    e.preventDefault()
    const data = JSON.parse(e.dataTransfer.getData('text/plain'))
    const { field, fromAxis } = data
    
    const newConfig = { ...chartConfig }
    
    // Remove from old location if moving between axes
    if (fromAxis !== 'available' && fromAxis !== toAxis) {
      const fromValue = newConfig[fromAxis as keyof ChartAxisConfig]
      if (Array.isArray(fromValue)) {
        const filteredValue = fromValue.filter(f => f !== field)
        if (filteredValue.length === 0) {
          delete newConfig[fromAxis as keyof ChartAxisConfig]
        } else {
          newConfig[fromAxis as keyof ChartAxisConfig] = filteredValue as any
        }
      } else if (fromValue === field) {
        delete newConfig[fromAxis as keyof ChartAxisConfig]
      }
    }
    
    // Add to new location
    const toValue = newConfig[toAxis as keyof ChartAxisConfig]
    const dropZoneConfig = chartTypeConfig.dropZones.find(dz => dz.key === toAxis)
    
    if (dropZoneConfig?.maxItems === 1) {
      // Single field - always store as string
      newConfig[toAxis as keyof ChartAxisConfig] = field as any
    } else {
      // Multiple fields - always store as array
      if (Array.isArray(toValue)) {
        if (!toValue.includes(field)) {
          newConfig[toAxis as keyof ChartAxisConfig] = [...toValue, field] as any
        }
      } else {
        newConfig[toAxis as keyof ChartAxisConfig] = [field] as any
      }
    }
    
    // Clear the dragged item tracking
    setDraggedItem(null)
    onChartConfigChange(newConfig)
  }

  const handleRemoveFromAxis = (field: string, fromAxis: string) => {
    const newConfig = { ...chartConfig }
    const value = newConfig[fromAxis as keyof ChartAxisConfig]
    
    if (Array.isArray(value)) {
      const filteredValue = value.filter(f => f !== field)
      if (filteredValue.length === 0) {
        delete newConfig[fromAxis as keyof ChartAxisConfig]
      } else {
        newConfig[fromAxis as keyof ChartAxisConfig] = filteredValue as any
      }
    } else if (value === field) {
      delete newConfig[fromAxis as keyof ChartAxisConfig]
    }
    
    onChartConfigChange(newConfig)
  }

  const handleReorder = (fromIndex: number, toIndex: number, axisKey: string) => {
    const newConfig = { ...chartConfig }
    const value = newConfig[axisKey as keyof ChartAxisConfig]
    
    // Only reorder if we have an array with multiple items
    if (Array.isArray(value) && value.length > 1 && fromIndex !== toIndex) {
      const newArray = [...value]
      const [movedItem] = newArray.splice(fromIndex, 1)
      newArray.splice(toIndex, 0, movedItem)
      newConfig[axisKey as keyof ChartAxisConfig] = newArray as any
      
      // Clear the dragged item tracking
      setDraggedItem(null)
      onChartConfigChange(newConfig)
    }
  }

  // Get unassigned fields
  const getUnassignedFields = () => {
    if (!availableFields) return { dimensions: [], timeDimensions: [], measures: [] }
    
    const assignedFields = new Set<string>()
    chartTypeConfig.dropZones.forEach(dz => {
      getFieldsForDropZone(dz.key).forEach(field => assignedFields.add(field))
    })
    
    // Exclude the currently dragged field only if it's being dragged FROM a configured axis
    // (not from available fields, where it should remain visible)
    if (draggedItem && draggedItem.fromAxis !== 'available') {
      assignedFields.add(draggedItem.field)
    }
    
    return {
      dimensions: availableFields.dimensions.filter(f => !assignedFields.has(f)),
      timeDimensions: availableFields.timeDimensions.filter(f => !assignedFields.has(f)),
      measures: availableFields.measures.filter(f => !assignedFields.has(f))
    }
  }

  const unassignedFields = getUnassignedFields()


  return (
    <div>
      {/* Available Fields - Hidden for skipQuery charts */}
      {!shouldSkipQuery && availableFields && (
        <div className="mb-4">
          <h4 className="text-xs font-semibold text-dc-text-secondary mb-2">Available Fields</h4>
          <div className="border border-dc-border rounded-lg p-2 bg-dc-surface-secondary">
            {(unassignedFields.dimensions.length > 0 ||
              unassignedFields.timeDimensions.length > 0 ||
              unassignedFields.measures.length > 0) ? (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-2 gap-y-4 sm:gap-y-2">
                {/* Dimensions Column */}
                <div className="pb-2 sm:pb-0">
                  <div className="text-xs text-dc-text-secondary mb-2 sm:mb-1 flex items-center">
                    <DimensionIcon className="w-3 h-3 mr-1 text-dc-text-muted" />
                    Dimensions
                  </div>
                  <div className="space-y-1">
                    {unassignedFields.dimensions.map(dim => {
                      const isBeingDragged = draggedItem && draggedItem.field === dim && draggedItem.fromAxis === 'available'
                      return (
                        <div
                          key={dim}
                          draggable
                          onDragStart={(e) => handleDragStart(e, dim, 'available')}
                          onDragEnd={handleDragEnd}
                          className={`bg-dc-dimension text-dc-dimension border border-dc-dimension hover:opacity-80 rounded-sm text-xs cursor-move px-3 py-2 sm:px-2 sm:py-1 truncate ${isBeingDragged ? 'opacity-50 cursor-grabbing' : ''}`}
                          title={dim}
                        >
                          {dim}
                        </div>
                      )
                    })}
                    {unassignedFields.dimensions.length === 0 && (
                      <div className="text-xs text-dc-text-muted italic">None</div>
                    )}
                  </div>
                </div>
                
                {/* Time Dimensions Column */}
                <div className="pb-2 sm:pb-0">
                  <div className="text-xs text-dc-text-secondary mb-2 sm:mb-1 flex items-center">
                    <TimeDimensionIcon className="w-3 h-3 mr-1 text-dc-text-muted" />
                    Time Dimensions
                  </div>
                  <div className="space-y-1">
                    {unassignedFields.timeDimensions.map(dim => {
                      const isBeingDragged = draggedItem && draggedItem.field === dim && draggedItem.fromAxis === 'available'
                      return (
                        <div
                          key={dim}
                          draggable
                          onDragStart={(e) => handleDragStart(e, dim, 'available')}
                          onDragEnd={handleDragEnd}
                          className={`bg-dc-time-dimension text-dc-time-dimension border border-dc-time-dimension hover:opacity-80 rounded-sm text-xs cursor-move px-3 py-2 sm:px-2 sm:py-1 truncate ${isBeingDragged ? 'opacity-50 cursor-grabbing' : ''}`}
                          title={dim}
                        >
                          {dim}
                        </div>
                      )
                    })}
                    {unassignedFields.timeDimensions.length === 0 && (
                      <div className="text-xs text-dc-text-muted italic">None</div>
                    )}
                  </div>
                </div>
                
                {/* Measures Column */}
                <div className="pb-2 sm:pb-0">
                  <div className="text-xs text-dc-text-secondary mb-2 sm:mb-1 flex items-center">
                    <MeasureIcon className="w-3 h-3 mr-1 text-dc-text-muted" />
                    Measures
                  </div>
                  <div className="space-y-1">
                    {unassignedFields.measures.map(measure => {
                      const isBeingDragged = draggedItem && draggedItem.field === measure && draggedItem.fromAxis === 'available'
                      return (
                        <div
                          key={measure}
                          draggable
                          onDragStart={(e) => handleDragStart(e, measure, 'available')}
                          onDragEnd={handleDragEnd}
                          className={`bg-dc-measure text-dc-measure border border-dc-measure hover:opacity-80 rounded-sm text-xs cursor-move px-3 py-2 sm:px-2 sm:py-1 truncate ${isBeingDragged ? 'opacity-50 cursor-grabbing' : ''}`}
                          title={measure}
                        >
                          {measure}
                        </div>
                      )
                    })}
                    {unassignedFields.measures.length === 0 && (
                      <div className="text-xs text-dc-text-muted italic">None</div>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-xs text-dc-text-muted text-center py-2">
                All fields have been assigned
              </div>
            )}
          </div>
        </div>
      )}

      {/* Chart Axis Configuration - Dynamic Drop Zones, Hidden for skipQuery charts */}
      {!shouldSkipQuery && (
        <div className="mb-4">
        <h4 className="text-xs font-semibold text-dc-text-secondary mb-2">Chart Configuration</h4>
        <div className="space-y-1">
          {chartTypeConfig.dropZones.map(dropZone => (
            <AxisDropZone
              key={dropZone.key}
              config={dropZone}
              fields={getFieldsForDropZone(dropZone.key)}
              onDrop={handleDrop}
              onRemove={handleRemoveFromAxis}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
              onDragOver={handleDragOver}
              getFieldStyling={getFieldStyling}
              onReorder={handleReorder}
              draggedItem={draggedItem}
            />
          ))}
        </div>
      </div>
      )}

      {/* Display Options */}
      {((chartTypeConfig.displayOptions && chartTypeConfig.displayOptions.length > 0) ||
        (chartTypeConfig.displayOptionsConfig && chartTypeConfig.displayOptionsConfig.length > 0)) && (
        <div className="mb-4">
          <h4 className="text-xs font-semibold text-dc-text-secondary mb-2">Display Options</h4>
          <div className="space-y-2">
            {/* Backward compatibility: Simple boolean display options */}
            {chartTypeConfig.displayOptions?.includes('showLegend') && (
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={displayConfig.showLegend ?? true}
                  onChange={(e) => onDisplayConfigChange({
                    ...displayConfig,
                    showLegend: e.target.checked
                  })}
                  className="rounded border-dc-border focus:ring-dc-accent"
                  style={{ color: 'var(--dc-primary)' }}
                />
                <span className="text-sm text-dc-text">Show Legend</span>
              </label>
            )}

            {chartTypeConfig.displayOptions?.includes('showGrid') && (
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={displayConfig.showGrid ?? true}
                  onChange={(e) => onDisplayConfigChange({
                    ...displayConfig,
                    showGrid: e.target.checked
                  })}
                  className="rounded border-dc-border focus:ring-dc-accent"
                  style={{ color: 'var(--dc-primary)' }}
                />
                <span className="text-sm text-dc-text">Show Grid</span>
              </label>
            )}

            {chartTypeConfig.displayOptions?.includes('showTooltip') && (
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={displayConfig.showTooltip ?? true}
                  onChange={(e) => onDisplayConfigChange({
                    ...displayConfig,
                    showTooltip: e.target.checked
                  })}
                  className="rounded border-dc-border focus:ring-dc-accent"
                  style={{ color: 'var(--dc-primary)' }}
                />
                <span className="text-sm text-dc-text">Show Tooltip</span>
              </label>
            )}

            {chartTypeConfig.displayOptions?.includes('stacked') && (
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={displayConfig.stacked ?? false}
                  onChange={(e) => onDisplayConfigChange({
                    ...displayConfig,
                    stacked: e.target.checked
                  })}
                  className="rounded border-dc-border focus:ring-dc-accent"
                  style={{ color: 'var(--dc-primary)' }}
                />
                <span className="text-sm text-dc-text">Stacked</span>
              </label>
            )}

            {chartTypeConfig.displayOptions?.includes('hideHeader') && (
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={displayConfig.hideHeader ?? false}
                  onChange={(e) => onDisplayConfigChange({
                    ...displayConfig,
                    hideHeader: e.target.checked
                  })}
                  className="rounded border-dc-border focus:ring-dc-accent"
                  style={{ color: 'var(--dc-primary)' }}
                />
                <span className="text-sm text-dc-text">Hide Header</span>
              </label>
            )}

            {/* New structured display options */}
            {chartTypeConfig.displayOptionsConfig?.map((option) => (
              <div key={option.key} className="space-y-1">
                {option.type === 'boolean' && (
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={displayConfig[option.key as keyof ChartDisplayConfig] ?? option.defaultValue ?? false}
                      onChange={(e) => onDisplayConfigChange({
                        ...displayConfig,
                        [option.key]: e.target.checked
                      })}
                      className="rounded border-dc-border focus:ring-dc-accent"
                      style={{ color: 'var(--dc-primary)' }}
                    />
                    <span className="text-sm text-dc-text">{option.label}</span>
                  </label>
                )}

                {option.type === 'string' && (
                  <div className="space-y-1">
                    <label className="text-sm text-dc-text-secondary">
                      {option.label}
                      {option.key === 'content' && (
                        <span className="text-xs text-dc-text-muted ml-1">(only headers, lists and links)</span>
                      )}
                    </label>
                    {option.key === 'content' ? (
                      <textarea
                        value={displayConfig[option.key as keyof ChartDisplayConfig] ?? option.defaultValue ?? ''}
                        onChange={(e) => onDisplayConfigChange({
                          ...displayConfig,
                          [option.key]: e.target.value
                        })}
                        placeholder={option.placeholder}
                        rows={8}
                        className="w-full px-2 py-1 text-sm border border-dc-border rounded-sm focus:ring-dc-accent focus:border-dc-accent font-mono resize-y bg-dc-surface text-dc-text"
                      />
                    ) : (
                      <input
                        type="text"
                        value={displayConfig[option.key as keyof ChartDisplayConfig] ?? option.defaultValue ?? ''}
                        onChange={(e) => onDisplayConfigChange({
                          ...displayConfig,
                          [option.key]: e.target.value
                        })}
                        placeholder={option.placeholder}
                        className="w-full px-2 py-1 text-sm border border-dc-border rounded-sm focus:ring-dc-accent focus:border-dc-accent bg-dc-surface text-dc-text"
                      />
                    )}
                    {option.description && (
                      <p className="text-xs text-dc-text-muted">{option.description}</p>
                    )}
                  </div>
                )}

                {option.type === 'paletteColor' && (
                  <div className="space-y-1">
                    <label className="text-sm text-dc-text-secondary">{option.label}</label>
                    <div className="flex flex-wrap gap-2">
                      {colorPalette?.colors.map((color, index) => {
                        const isSelected = (displayConfig[option.key as keyof ChartDisplayConfig] ?? option.defaultValue ?? 0) === index
                        return (
                          <button
                            key={index}
                            type="button"
                            onClick={() => onDisplayConfigChange({
                              ...displayConfig,
                              [option.key]: index
                            })}
                            className={`w-8 h-8 rounded border-2 transition-all duration-200 hover:scale-110 focus:outline-hidden focus:ring-2 focus:ring-dc-accent focus:ring-offset-1 ${
                              isSelected
                                ? 'ring-2 ring-offset-1 scale-110'
                                : 'hover:border-dc-text-muted'
                            }`}
                            style={{
                              backgroundColor: color,
                              borderColor: isSelected ? 'var(--dc-primary)' : 'var(--dc-border)'
                            }}
                            title={`Color ${index + 1}: ${color}`}
                          />
                        )
                      }) || [
                        // Fallback if no palette available
                        <button
                          key={0}
                          type="button"
                          onClick={() => onDisplayConfigChange({
                            ...displayConfig,
                            [option.key]: 0
                          })}
                          className="w-8 h-8 rounded-sm border-2 ring-2 ring-offset-1"
                          style={{
                            backgroundColor: '#8884d8',
                            borderColor: 'var(--dc-primary)',
                            boxShadow: '0 0 0 2px var(--dc-primary)'
                          }}
                          title="Default Color"
                        />
                      ]}
                    </div>
                    {option.description && (
                      <p className="text-xs text-dc-text-muted">{option.description}</p>
                    )}
                  </div>
                )}

                {option.type === 'number' && (
                  <div className="space-y-1">
                    <label className="text-sm text-dc-text-secondary">{option.label}</label>
                    <input
                      type="number"
                      value={displayConfig[option.key as keyof ChartDisplayConfig] ?? option.defaultValue ?? 0}
                      onChange={(e) => onDisplayConfigChange({
                        ...displayConfig,
                        [option.key]: e.target.value === '' ? undefined : Number(e.target.value)
                      })}
                      placeholder={option.placeholder}
                      min={option.min}
                      max={option.max}
                      step={option.step}
                      className="w-full px-2 py-1 text-sm border border-dc-border rounded-sm focus:ring-dc-accent focus:border-dc-accent bg-dc-surface text-dc-text"
                    />
                    {option.description && (
                      <p className="text-xs text-dc-text-muted">{option.description}</p>
                    )}
                  </div>
                )}

                {option.type === 'select' && (
                  <div className="space-y-1">
                    <label className="text-sm text-dc-text-secondary">{option.label}</label>
                    <select
                      value={displayConfig[option.key as keyof ChartDisplayConfig] ?? option.defaultValue ?? ''}
                      onChange={(e) => onDisplayConfigChange({
                        ...displayConfig,
                        [option.key]: e.target.value
                      })}
                      className="w-full px-2 py-1 text-sm border border-dc-border rounded-sm focus:ring-dc-accent focus:border-dc-accent bg-dc-surface text-dc-text"
                    >
                      {option.options?.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                    {option.description && (
                      <p className="text-xs text-dc-text-muted">{option.description}</p>
                    )}
                  </div>
                )}

                {option.type === 'color' && (
                  <div className="space-y-1">
                    <label className="text-sm text-dc-text-secondary">{option.label}</label>
                    <div className="flex items-center space-x-2">
                      <input
                        type="color"
                        value={displayConfig[option.key as keyof ChartDisplayConfig] ?? option.defaultValue ?? '#8884d8'}
                        onChange={(e) => onDisplayConfigChange({
                          ...displayConfig,
                          [option.key]: e.target.value
                        })}
                        className="w-12 h-8 border border-dc-border rounded-sm cursor-pointer"
                      />
                      <input
                        type="text"
                        value={displayConfig[option.key as keyof ChartDisplayConfig] ?? option.defaultValue ?? '#8884d8'}
                        onChange={(e) => onDisplayConfigChange({
                          ...displayConfig,
                          [option.key]: e.target.value
                        })}
                        placeholder={option.placeholder || '#8884d8'}
                        className="flex-1 px-2 py-1 text-sm border border-dc-border rounded-sm focus:ring-dc-accent focus:border-dc-accent bg-dc-surface text-dc-text"
                      />
                    </div>
                    {option.description && (
                      <p className="text-xs text-dc-text-muted">{option.description}</p>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}